/* eslint-disable jira/filename-case */
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { createSessionExpirableStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/session-storage/index.tsx';
import type { ExpirableWebStorageProvider } from '@jira/platform__browser-storage-providers/src/types.tsx';
import { KEY_HAS_NAVIGATED_FROM_LINK, ACCOUNT_ID } from '../constants.tsx';

type AccountId = string;

const DAYS_BEFORE_EXPIRY = 1;

const DAY = 24 * 60 * 60 * 1000;

const getKey = ({ accountId, key }: { accountId: AccountId; key: string }) => `${accountId}.${key}`;

const getExpiryTime = () => {
	const now = Date.now();
	return now + DAYS_BEFORE_EXPIRY * DAY;
};

let localStorage: {
	provider: ExpirableWebStorageProvider;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	get: (arg: { accountId: AccountId; key: string }) => any;
	set: (arg: { accountId: AccountId; key: string; value: unknown }) => void;
	remove: (arg: { accountId: AccountId; key: string }) => void;
};

export const localStorageProvider = (): {
	provider: ExpirableWebStorageProvider;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	get: (arg: { accountId: AccountId; key: string }) => any;
	set: (arg: { accountId: AccountId; key: string; value: unknown }) => void;
	remove: (arg: { accountId: AccountId; key: string }) => void;
} => {
	if (localStorage) {
		// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
		return localStorage;
	}

	const provider = createSessionExpirableStorageProvider('jsw-onboarding-checklist');
	try {
		provider.removeExpiredKeys();
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		fireErrorAnalytics({
			meta: {
				id: 'localStorageProviderKeyRemoval',
				packageName: 'jiraOnboardingQuickstartCore',
			},
			error: e,
		});
	}

	const get = ({ accountId, key }: { accountId: AccountId; key: string }) =>
		provider.get(getKey({ accountId, key }));

	const set = ({ accountId, key, value }: { accountId: AccountId; key: string; value: unknown }) =>
		// @browser-storage setter
		provider.set(getKey({ accountId, key }), value, getExpiryTime());

	const remove = ({ accountId, key }: { accountId: AccountId; key: string }) =>
		provider.remove(getKey({ accountId, key }));

	localStorage = {
		provider,
		remove,
		get,
		set,
	};

	// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
	return localStorage;
};

export const setLocalStorageHasNavigatedFromLink = (value: string): void => {
	localStorageProvider().set({
		accountId: ACCOUNT_ID,
		key: KEY_HAS_NAVIGATED_FROM_LINK,
		value,
	});
};
